const sessionDefaults = {
    screen: "menu", // "menu" | "session"
    mode: null, // "decision" or "review"
    sessionOrigin: null, // "buzzer" or "study"
    ratingEligible: false,
    questions: [],
    currentIndex: 0,
    phase: "ready",
    missedQuestions: [],
    reviewResults: [],
    currentQuestion: null,
    answerRevealed: false,
    readyStartedAt: null,
    readingStartedAt: null,
    buzzWindowStartedAt: null,
    buzzedAt: null,

    buzzDisabled: false,
    didBuzz: false,
    didPass: false,
    pendingReactionMs: null,

    currentOutcome: null,
    currentScoreDelta: 0,

    score: 0,
    results: [],
    scoreFeedback: null,

    correctBuzzes: 0,
    incorrectBuzzes: 0,
    goodPasses: 0,
    missedOpportunities: 0,
    tooEarlyCount: 0,
    tooLateCount: 0,
};

const initialCardState = {
    ...sessionDefaults,
    currentStreak: Number(localStorage.getItem("currentStreak")) || 0,
    bestStreak: Number(localStorage.getItem("bestStreak")) || 0,
    consecutivePasses: Number(localStorage.getItem("consecutivePasses")) || 0,
};

function cardReducer(state, action) {
    switch (action.type) {
        case "OPEN_SETTINGS":
            return {
                ...state,
                screen: "settings",
            };

        case "EXIT_SETTINGS":
            return {
                ...state,
                screen: "menu",
            };

        case "OPEN_STATS":
            return {
                ...state,
                screen: "stats",
            };

        case "START_SESSION": {
            return {
                ...sessionDefaults,
                ratingEligible: true,
                currentStreak: state.currentStreak ?? 0,
                bestStreak: state.bestStreak ?? 0,
                consecutivePasses: state.consecutivePasses ?? 0,
                screen: "session",
                mode: action.mode,

                sessionOrigin:
                    action.mode === "decision"
                        ? "buzzer"
                        : "study",

                questions: action.questions,
                currentQuestion: getCurrentQuestion(action.questions, 0),
                phase: action.mode === "review" ? "answering" : "ready",
                answerRevealed: false,
            };
        }

        case "EXIT_TO_MENU":
            return {
                ...sessionDefaults,
                screen: "menu",
                mode: state.mode,
                currentStreak: state.currentStreak ?? 0,
                bestStreak: state.bestStreak ?? 0,
                consecutivePasses: state.consecutivePasses ?? 0,
            };



        case "START_ROUND": {
            return {
                ...state,
                mode: state.mode,
                phase: "ready",
                currentQuestion: getCurrentQuestion(state.questions, state.currentIndex),
                answerRevealed: false,

                readyStartedAt: action.now,
                readingStartedAt: null,
                buzzWindowStartedAt: null,
                buzzedAt: null,

                didBuzz: false,
                didPass: false,
                pendingReactionMs: null,

                currentOutcome: null,
                currentScoreDelta: 0,
                currentStreak: state.currentStreak,
                bestStreak: state.bestStreak,
                consecutivePasses: state.consecutivePasses,
            };
        }

        case "START_READING": {
            return {
                ...state,
                mode: state.mode,
                phase: "reading",
                readingStartedAt: action.now,
            };
        }

        case "OPEN_BUZZ_WINDOW": {
            return {
                ...state,
                mode: state.mode,
                phase: "buzzable",
                buzzWindowStartedAt: action.now,
            };
        }

        case "PRESS_BUZZ": {
            if (state.phase !== "buzzable" || !state.buzzWindowStartedAt) return state;

            const reactionMs = action.now - state.buzzWindowStartedAt;

            return {
                ...state,
                mode: state.mode,
                phase: "answering",
                didBuzz: true,
                buzzedAt: action.now,
                pendingReactionMs: reactionMs,
                consecutivePasses: 0,
                answerRevealed: true,
            };
        }

        case "BUZZ_TOO_EARLY":
            return {
                ...state,
                buzzDisabled: true,
            };

        case "ENABLE_BUZZ":
            return {
                ...state,
                buzzDisabled: false,
            };

        case "PRESS_PASS": {
            const breaksStreak = state.consecutivePasses >= 1;
            const passedQuestion = state.currentQuestion;
            return {
                ...state,
                mode: state.mode,
                phase: "feedback",
                didPass: true,
                answerRevealed: true,
                currentOutcome: "good_pass",
                currentScoreDelta: 0,
                currentStreak: breaksStreak ? 0 : state.currentStreak,
                goodPasses: state.goodPasses + 1,
                consecutivePasses: breaksStreak ? 0 : 1,
                scoreFeedback: null,
                missedQuestions:
                    state.mode === "decision" && passedQuestion
                        ? [...state.missedQuestions, passedQuestion]
                        : state.missedQuestions,
                results: state.currentQuestion
                    ? [
                        ...state.results,
                        {
                            id: state.currentQuestion.id,
                            outcome: "good_pass",
                            scoreDelta: 0,
                            reactionMs: null,
                            category: state.currentQuestion.category,
                            categoryId: state.currentQuestion.category_id,
                        },
                    ]
                    : state.results,
            };
        }

        case "ANSWER_CORRECT_BUZZER": {
            const basePoints = 1000;
            const reactionMs = state.pendingReactionMs;
            const streak = state.currentStreak + 1;

            const speedBonus =
                reactionMs < 300 ? 1000 :
                    reactionMs < 500 ? 500 :
                        reactionMs < 800 ? 250 :
                            0;

            const precisionBonus = Math.max(0, 1000 - reactionMs);

            const multiplier =
                streak >= 25 ? 5 :
                    streak >= 15 ? 4 :
                        streak >= 10 ? 3 :
                            streak >= 5 ? 2 :
                                streak >= 3 ? 1.5 :
                                    streak >= 2 ? 1.25 :
                                        1;

            const rawScoreDelta = (basePoints + speedBonus + precisionBonus) * multiplier;
            const scoreDelta = Math.round(rawScoreDelta / 50) * 50;

            let callout = "";

            if (streak === 100) {
                callout = "⚜️ IMMORTAL ⚜️";
            } else if (streak === 50) {
                callout = "☄️ MYTHIC ☄️";
            } else if (streak === 25) {
                callout = "👑 LEGENDARY 👑";
            } else if (streak === 15) {
                callout = "🌋 DOMINATING 🌋";
            } else if (streak === 10) {
                callout = "🚀 UNSTOPPABLE 🚀";
            } else if (scoreDelta >= 10000 && reactionMs < 400 && streak % 10 === 0) {
                callout = "💥 MASSIVE PLAY 💥";
            } else if (streak === 5) {
                callout = "🔥 ON FIRE 🔥";
            } else if (streak === 3) {
                callout = "⚡ HEATING UP ⚡";
            } else if (reactionMs < 200) {
                callout = "⚡ LIGHTNING FAST ⚡";
            }

            const feedback = {
                basePoints: basePoints,
                speedBonus: speedBonus,
                streakMultiplier: multiplier,
                totalPoints: scoreDelta,
                reactionMs: reactionMs,
                streak: streak,
                callout: callout,
            };

            const reviewEntry =
                state.mode === "review" && state.currentQuestion
                    ? {
                        id: state.currentQuestion.id,
                        corrected: true,
                    }
                    : null;

            return {
                ...state,
                mode: state.mode,
                phase: "feedback",
                currentOutcome: "correct_buzz",
                currentScoreDelta: scoreDelta,
                score: state.score + scoreDelta,
                correctBuzzes: state.correctBuzzes + 1,
                currentStreak: streak,
                bestStreak: Math.max(state.bestStreak, streak),
                consecutivePasses: 0,
                scoreFeedback: feedback,
                reviewResults: reviewEntry
                    ? [...state.reviewResults, reviewEntry]
                    : state.reviewResults,
                results: state.currentQuestion
                    ? [
                        ...state.results,
                        {
                            id: state.currentQuestion.id,
                            outcome: "correct_buzz",
                            scoreDelta: 100,
                            reactionMs: state.pendingReactionMs,
                            category: state.currentQuestion.category,
                            categoryId: state.currentQuestion.category_id,
                        },
                    ]
                    : state.results,
            };
        }

        case "ANSWER_CORRECT_REVIEW": {
            const reviewEntry =
                state.mode === "review" && state.currentQuestion
                    ? {
                        id: state.currentQuestion.id,
                        corrected: true,
                    }
                    : null;

            return {
                ...state,
                mode: state.mode,
                phase: "feedback",
                currentOutcome: "correct_buzz",
                correctBuzzes: state.correctBuzzes + 1,
                reviewResults: reviewEntry
                    ? [...state.reviewResults, reviewEntry]
                    : state.reviewResults,
                results: state.currentQuestion
                    ? [
                        ...state.results,
                        {
                            id: state.currentQuestion.id,
                            outcome: "correct_buzz",
                            scoreDelta: 100,
                            reactionMs: state.pendingReactionMs,
                            category: state.currentQuestion.category,
                            categoryId: state.currentQuestion.category_id,
                        },
                    ]
                    : state.results,
            };
        }

        case "ANSWER_INCORRECT_BUZZER": {
            const missedQuestion = state.currentQuestion;
            const reviewEntry =
                state.mode === "review" && state.currentQuestion
                    ? {
                        id: state.currentQuestion.id,
                        corrected: false,
                    }
                    : null;

            return {
                ...state,
                mode: state.mode,
                phase: "feedback",
                currentOutcome: "incorrect_buzz",
                currentScoreDelta: -1000,
                score: state.score - 1000,
                currentStreak: 0,
                scoreFeedback: null,
                incorrectBuzzes: state.incorrectBuzzes + 1,
                missedQuestions:
                    state.mode === "decision" && missedQuestion
                        ? [...state.missedQuestions, missedQuestion]
                        : state.missedQuestions,
                reviewResults: reviewEntry
                    ? [...state.reviewResults, reviewEntry]
                    : state.reviewResults,
                results: state.currentQuestion
                    ? [
                        ...state.results,
                        {
                            id: state.currentQuestion.id,
                            outcome: "incorrect_buzz",
                            scoreDelta: -1000,
                            reactionMs: state.pendingReactionMs,
                            category: state.currentQuestion.category,
                            categoryId: state.currentQuestion.category_id,
                        },
                    ]
                    : state.results,
            };
        }

        case "ANSWER_INCORRECT_REVIEW": {
            const missedQuestion = state.currentQuestion;
            const reviewEntry =
                state.mode === "review" && state.currentQuestion
                    ? {
                        id: state.currentQuestion.id,
                        corrected: false,
                    }
                    : null;

            return {
                ...state,
                mode: state.mode,
                phase: "feedback",
                currentOutcome: "incorrect_buzz",
                incorrectBuzzes: state.incorrectBuzzes + 1,
                missedQuestions:
                    state.mode === "decision" && missedQuestion
                        ? [...state.missedQuestions, missedQuestion]
                        : state.missedQuestions,
                reviewResults: reviewEntry
                    ? [...state.reviewResults, reviewEntry]
                    : state.reviewResults,
                results: state.currentQuestion
                    ? [
                        ...state.results,
                        {
                            id: state.currentQuestion.id,
                            outcome: "incorrect_buzz",
                            scoreDelta: -50,
                            reactionMs: state.pendingReactionMs,
                            category: state.currentQuestion.category,
                            categoryId: state.currentQuestion.category_id,
                        },
                    ]
                    : state.results,
            };
        }       

        case "AUTO_TIMEOUT_PASS": {
            const missedQuestion = state.currentQuestion;
            const reviewEntry =
                state.mode === "review" && state.currentQuestion
                    ? {
                        id: state.currentQuestion.id,
                        corrected: false,
                    }
                    : null;

            return {
                ...state,
                mode: state.mode,
                phase: "feedback",
                didPass: true,
                answerRevealed: true,
                currentOutcome: "good_pass",
                currentScoreDelta: 0,
                currentStreak: 0,
                consecutivePasses: 0,
                scoreFeedback: null,
                goodPasses: state.goodPasses + 1,
                results: state.currentQuestion
                    ? [
                        ...state.results,
                        {
                            id: state.currentQuestion.id,
                            outcome: "good_pass",
                            scoreDelta: 0,
                            reactionMs: null,
                            category: state.currentQuestion.category,
                            categoryId: state.currentQuestion.category_id,
                        },
                    ]
                    : state.results,
                missedQuestions:
                    state.mode === "decision" && missedQuestion
                        ? [...state.missedQuestions, missedQuestion]
                        : state.missedQuestions,
                reviewResults: reviewEntry
                    ? [...state.reviewResults, reviewEntry]
                    : state.reviewResults,
            };
        }

        case "ADVANCE_ROUND": {
            const nextIndex = state.currentIndex + 1;
            const nextQuestion = getCurrentQuestion(state.questions, nextIndex);
            if (!nextQuestion) {
                return {
                    ...state,
                    mode: state.mode,
                    currentIndex: nextIndex,
                    currentQuestion: null,
                    phase: "complete",
                    answerRevealed: false,
                };
            }

            return {
                ...state,
                mode: state.mode,
                currentIndex: nextIndex,
                currentQuestion: nextQuestion,
                phase: "ready",
                answerRevealed: false,

                readyStartedAt: action.now,
                readingStartedAt: null,
                buzzWindowStartedAt: null,
                buzzedAt: null,
                buzzDisabled: false,

                didBuzz: false,
                didPass: false,
                pendingReactionMs: null,

                currentOutcome: null,
                currentScoreDelta: 0,
            };
        }

        case "FINISH_SESSION": {
            return {
                ...state,
                mode: state.mode,
                phase: "complete",
            };
        }

        case "START_REVIEW": {
            return buildReviewState(state, state.missedQuestions);
        }

        case "REVIEW_AGAIN": {
            const reviewQuestions = getMissedReviewQuestions(state);
            return buildReviewState(state, reviewQuestions);
        }

        case "REVEAL_ANSWER":
            return {
                ...state,
                mode: state.mode,
                phase: "answering",
                answerRevealed: true,
            };

        default:
            return state;
    }
}

function getCurrentQuestion(questions, index) {
    return questions[index] ?? null;
}

function getMissedReviewQuestions(state) {
    const missedReviewIds = state.reviewResults.filter(r => !r.corrected).map(r => r.id);
    return state.questions.filter(q => missedReviewIds.includes(q.id));
}

function buildReviewState(baseState, reviewQuestions) {
    if (!reviewQuestions.length) return baseState;

    return {
        ...sessionDefaults,
        screen: "session",
        mode: "review",
        ratingEligible: false,
        sessionOrigin: baseState.sessionOrigin,
        questions: reviewQuestions,
        currentIndex: 0,
        currentQuestion: reviewQuestions[0],
        phase: "ready",
        answerRevealed: false,
        missedQuestions: reviewQuestions,
        reviewResults: [],
        currentStreak: baseState.currentStreak ?? 0,
        bestStreak: baseState.bestStreak ?? 0,
        consecutivePasses: baseState.consecutivePasses ?? 0,
    };
}

export { initialCardState, cardReducer }
